import React from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { ownerApi } from "../../../../api/owner";
import { formatApiError } from "../../../../api/client";

const STATUS_LABELS = { active: "Activo", paused: "Pausado", cancelled: "Cancelado" };

export default function OwnerClientProjectLinkDialog({ open, client, linkedProjectIds = [], onClose, onLinked }) {
  const [query, setQuery] = React.useState("");
  const [projects, setProjects] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [savingId, setSavingId] = React.useState("");
  const [error, setError] = React.useState("");
  const linkedSet = React.useMemo(() => new Set(linkedProjectIds.map(String)), [linkedProjectIds]);

  React.useEffect(() => {
    if (!open) return;
    setQuery("");
    setError("");
  }, [open]);

  React.useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    const timeout = setTimeout(async () => {
      setLoading(true);
      setError("");
      try {
        const response = await ownerApi.searchWebProfitProjects({ query, limit: 24 });
        if (!cancelled) setProjects(response?.projects || []);
      } catch (err) {
        if (!cancelled) setError(formatApiError(err, "No pude buscar proyectos WebProfit."));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 220);
    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [open, query]);

  const link = async (projectId) => {
    setSavingId(projectId);
    setError("");
    try {
      const response = await ownerApi.linkClientWebProfitProject(client.id || client._id, projectId, true);
      onLinked?.(response?.context || null);
      onClose?.();
    } catch (err) {
      setError(formatApiError(err, "No pude vincular el proyecto."));
    } finally {
      setSavingId("");
    }
  };

  return (
    <Dialog open={open} onClose={savingId ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Typography sx={{ fontWeight: 1000 }}>Vincular Project WebProfit</Typography>
        <Typography sx={{ mt: .25, color: "text.secondary", fontSize: ".8rem" }}>
          Esto crea sólo una relación comercial en Cliente 360. No agrega miembros, no cambia permisos y no modifica billing.
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={1.1} sx={{ pt: .7 }}>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Buscar proyecto"
            placeholder="Nombre, slug o dominio"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon fontSize="small" /></InputAdornment> }}
          />
          {error ? <Alert severity="error">{error}</Alert> : null}
          {loading ? <Box sx={{ py: 4, textAlign: "center" }}><CircularProgress size={28} /></Box> : (
            <Stack spacing={.65}>
              {projects.map((project) => {
                const alreadyLinked = linkedSet.has(String(project.id));
                return (
                  <Button
                    key={project.id}
                    disabled={Boolean(savingId) || alreadyLinked}
                    onClick={() => link(project.id)}
                    sx={{ p: 1.05, justifyContent: "flex-start", textTransform: "none", border: "1px solid", borderColor: "divider", borderRadius: 2.2 }}
                  >
                    <Box sx={{ minWidth: 0, flex: 1, textAlign: "left" }}>
                      <Typography noWrap sx={{ fontWeight: 950, fontSize: ".84rem" }}>{project.name}</Typography>
                      <Typography noWrap sx={{ mt: .12, color: "text.secondary", fontSize: ".7rem" }}>
                        {project.slug} · {STATUS_LABELS[project.status] || project.status}{project.publicUrl ? ` · ${project.publicUrl}` : ""}
                      </Typography>
                    </Box>
                    {savingId === project.id ? <CircularProgress size={18} /> : <Typography sx={{ ml: 1, fontSize: ".7rem", fontWeight: 900 }}>{alreadyLinked ? "Ya vinculado" : <LinkRoundedIcon fontSize="small" />}</Typography>}
                  </Button>
                );
              })}
              {!projects.length && !loading ? <Typography sx={{ py: 3, textAlign: "center", color: "text.secondary", fontSize: ".8rem" }}>No encontré proyectos con esa búsqueda.</Typography> : null}
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.2 }}>
        <Button onClick={onClose} disabled={Boolean(savingId)} sx={{ textTransform: "none", fontWeight: 850 }}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}
